/**
 * `<OutlineEditor/>` — the keyboard-first outline editor (B2.3 full-screen +
 * B2.4 split-view left pane share this one implementation, §A7 / the brief:
 * "don't fork two outline implementations").
 *
 * Issue #759 (Phase 2, PR-2). The "type a nested list and a tree appears"
 * gesture that makes decomposition effortless:
 *   - **Enter**       — new sibling below the current row (POST, same parent,
 *                       order after current); the new row opens in edit mode.
 *   - **Tab**         — indent: re-parent under the preceding sibling (PATCH).
 *   - **Shift-Tab**   — outdent: re-parent to the grandparent (PATCH).
 *   - **Backspace**   — on an empty *just-created* row: remove it (don't keep
 *                       empties); otherwise normal text editing.
 *   - **@**           — open the member picker inline → assign (POST).
 *   - **drag a row**  — reorder among siblings (PATCH order_index).
 *   - **Esc**         — stop editing, keep the title.
 *
 * It is a thin shell: every gesture resolves (via the pure `outlineModel`) to
 * exactly one mutation, which the parent wires through `useTaskNodes`'s hooks.
 * Selection + the inline-edit cursor live in the shared `treeStore`, so a row
 * edited here lights up on the tree canvas too (the two-way sync, B2.4).
 *
 * Brand (§A9): factual copy, no scores; the hint strip + placeholders are the
 * gallery's verbatim text.
 *
 * Editorial revamp (#1043, Wave 1): theme-aware (B2.3 / B2.4-left). EDITORIAL
 * (flag on) is the wireframe `V_B23` — a warm-paper card (`c.cardBg` + `c.rule`
 * hairline) holding the `.t-outline` rows, a mono `OUTLINE EDITOR — KEYBOARD
 * FIRST` kicker above it, and the keyboard-hint strip rendered with editorial
 * `Kbd` chips (mono, `c.rule2` border, 2px bottom-border). LEGACY (flag off)
 * is byte-identical to today. The exact hint copy + every `data-testid` are
 * preserved across both modes; only the look forks on theme.
 */

import { type KeyboardEvent, type ReactNode, useEffect, useState } from "react";

import type { TaskMember } from "../members/useTaskTeamMembers";
import {
  type CreateTarget,
  type MoveTarget,
  findNode,
  indentTarget,
  outdentTarget,
  reorderTarget,
  rootCreateTarget,
  siblingCreateTarget,
  visibleOutlineRows,
} from "../outline/outlineModel";
import { useMapTheme } from "../theme/MapThemeContext";
import type { TreeNode } from "../tree/buildTree";
import { PALETTE } from "../tree/palette";
import type { TaskState } from "../tree/state";
import { AssignPopover } from "./AssignPopover";
import { OutlineCommandMenu } from "./OutlineCommandMenu";
import { OutlineRow } from "./OutlineRow";

/** A stable empty collapsed-set, so the no-collapse path never re-allocates. */
const EMPTY_COLLAPSED: ReadonlySet<string> = new Set<string>();

/**
 * `<Kbd/>` — the editorial keyboard-cap chip (`tarii.css` `.kbd`): mono, a
 * `c.rule2` hairline with a 2px bottom-border (the keycap "lip"), cream ground.
 * Used in the B2.3 hint strip; legacy renders the hint as plain text.
 */
function Kbd({ children }: { children: ReactNode }) {
  const theme = useMapTheme();
  const c = theme.palette;
  return (
    <span
      data-testid="outline-kbd"
      className="inline-block rounded text-[10.5px] leading-none"
      style={{
        fontFamily: theme.fonts.mono,
        padding: "1px 5px",
        border: `1px solid ${c.rule2}`,
        borderBottomWidth: 2,
        backgroundColor: c.cardBg,
        color: c.slate,
      }}
    >
      {children}
    </span>
  );
}

export interface OutlineEditorProps {
  roots: TreeNode[];
  members: TaskMember[];
  selectedId: string | null;
  editingNodeId: string | null;
  /**
   * Single-click on a row title: open the node's detail drawer (also selects
   * it). Issue #960 — clicking a row opens the "expanded version" beside the
   * outline; rename moves to double-click. The `openNode` action in `treeStore`
   * also sets `selectedId`, so a separate `onSelect` callback is not needed for
   * mouse interactions.
   */
  onOpenNode: (id: string) => void;
  /** Begin / stop inline-editing a node's title (null = stop). */
  onSetEditing: (id: string | null) => void;
  /** Commit a renamed title. */
  onRename: (id: string, title: string) => void;
  /** Create a node at the given target; the new node opens in edit mode. */
  onCreate: (target: CreateTarget, opts?: { focusEmpty?: boolean }) => void;
  /** Re-parent a node (indent / outdent). */
  onMove: (id: string, target: MoveTarget) => void;
  /** Re-order a node among its siblings. */
  onReorder: (id: string, orderIndex: number) => void;
  /** Toggle a member's assignment on a node. */
  onAssignToggle: (id: string, membershipId: string, assign: boolean) => void;
  /** Remove a node entirely (Backspace on an empty new row → archive). */
  onRemove: (id: string) => void;
  /**
   * Set a row's state from the "/" command menu (#1043). Wires to the SAME
   * `patchNode` state path the canvas card + drawer use — passed in from
   * `MapEditor` (mirrors `onAssignToggle`). When omitted the menu's "Change
   * state" item is hidden (the menu degrades to dependency-only / nothing).
   */
  onSetState?: (id: string, state: TaskState) => void;
  /**
   * Arm "add a dependency" FROM a row (the "/" command menu, #1043). Reuses the
   * existing click-to-link mechanism (`treeStore.linkArm` / the canvas link
   * gesture, #904): arming primes the row as the SOURCE, then the user clicks a
   * target node on the canvas. Supplied only when the dependency layer is on;
   * when omitted the menu hides the "Add a dependency" item.
   */
  onArmDependency?: (id: string) => void;
  /**
   * The shared `treeStore.collapsed` set (B8.3). When supplied the outline
   * hides descendants of collapsed nodes AND shows a collapse chevron — the
   * SAME set the canvas reads, so the two surfaces fold in lock-step. Omitted ⇒
   * every row is shown with no chevron (byte-unchanged legacy behaviour).
   */
  collapsed?: ReadonlySet<string>;
  /** Toggle a node's collapsed state (the shared `treeStore.toggleCollapse`). */
  onToggleCollapse?: (id: string) => void;
  /** Compact spacing for the split-view pane (vs. roomy full-screen). */
  compact?: boolean;
}

export function OutlineEditor({
  roots,
  members,
  selectedId,
  editingNodeId,
  onOpenNode,
  onSetEditing,
  onRename,
  onCreate,
  onMove,
  onReorder,
  onAssignToggle,
  onRemove,
  onSetState,
  onArmDependency,
  collapsed,
  onToggleCollapse,
  compact = false,
}: OutlineEditorProps) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  // Collapse-aware rows: descendants of a collapsed node are hidden, reusing the
  // shared `treeStore.collapsed` set so the outline + canvas fold together. With
  // no set supplied (or an empty one) this is the full depth-first list as before.
  const collapsedSet = collapsed ?? EMPTY_COLLAPSED;
  const rows = visibleOutlineRows(roots, collapsedSet);

  // Draft text for the row currently being edited. Seeded from the node's
  // title when editing starts; committed on Enter/Tab/blur.
  const [draft, setDraft] = useState("");
  // The row anchoring the @-assign picker (null = closed).
  const [assignPickerFor, setAssignPickerFor] = useState<string | null>(null);
  // The row anchoring the "/" command menu (null = closed).
  const [commandMenuFor, setCommandMenuFor] = useState<string | null>(null);
  // Drag state for sibling reorder.
  const [dragId, setDragId] = useState<string | null>(null);

  const editingNode = editingNodeId ? findNode(roots, editingNodeId) : null;

  // Seed the draft whenever the edit cursor moves to a different node.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-seed only when the edited node changes
  useEffect(() => {
    if (editingNode) setDraft(editingNode.title);
  }, [editingNodeId]);

  const commitDraft = (node: TreeNode) => {
    const next = draft.trim();
    if (next && next !== node.title) onRename(node.id, next);
  };

  // The "/" command menu is offered only when there's something to do with it:
  // a state setter and/or a dependency arm. (Off → "/" types a literal slash.)
  const commandMenuAvailable = Boolean(onSetState || onArmDependency);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, node: TreeNode) => {
    // @ opens the inline assign picker without inserting the character.
    if (e.key === "@") {
      e.preventDefault();
      setAssignPickerFor(node.id);
      return;
    }

    // "/" opens the command menu (change state / add a dependency, #1043) —
    // but ONLY on an EMPTY row, so typing a literal slash mid-title is never
    // trapped (a titled row keeps "/" as a character). Escape closes the menu
    // and restores normal typing. Mirrors the @-picker's "open without
    // inserting the char" gesture.
    if (e.key === "/" && draft.length === 0 && commandMenuAvailable) {
      e.preventDefault();
      setCommandMenuFor(node.id);
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      commitDraft(node);
      // New sibling below this row; it opens in edit mode (focusEmpty).
      onCreate(siblingCreateTarget(roots, node), { focusEmpty: true });
      return;
    }

    if (e.key === "Tab") {
      e.preventDefault();
      commitDraft(node);
      const target = e.shiftKey ? outdentTarget(roots, node) : indentTarget(roots, node);
      if (target) onMove(node.id, target);
      return;
    }

    if (e.key === "Backspace" && draft.length === 0) {
      // Empty row → don't keep empties: remove it and step the cursor back to
      // the previous row (so the keyboard flow continues without a dead stop).
      e.preventDefault();
      const idx = rows.findIndex((r) => r.node.id === node.id);
      const prev = idx > 0 ? rows[idx - 1]?.node : null;
      onRemove(node.id);
      onSetEditing(prev ? prev.id : null);
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      // Escape first dismisses an open overlay (the "/" command menu or the
      // @-assign picker) and KEEPS the row in edit mode so typing resumes;
      // only a second Escape (no overlay open) stops editing.
      if (commandMenuFor === node.id) {
        setCommandMenuFor(null);
        return;
      }
      if (assignPickerFor === node.id) {
        setAssignPickerFor(null);
        return;
      }
      onSetEditing(null);
    }
  };

  const handleBlur = (node: TreeNode) => {
    // Committing on blur keeps edits when the user clicks away. The assign
    // picker + the "/" command menu own their own focus, so don't
    // close-on-blur (or commit) while either is open for this row.
    if (assignPickerFor === node.id || commandMenuFor === node.id) return;
    commitDraft(node);
    onSetEditing(null);
  };

  // ── Drag-reorder among siblings ───────────────────────────────────────
  const handleDrop = (target: TreeNode) => {
    if (!dragId || dragId === target.id) {
      setDragId(null);
      return;
    }
    const dragged = findNode(roots, dragId);
    // Only reorder *within the same parent* here (re-parenting via drag is the
    // tree canvas's job — §A7). A cross-parent drag in the outline is ignored.
    if (dragged && dragged.parentId === target.parentId) {
      const reorder = reorderTarget(roots, dragged, indexOfSibling(target, roots));
      if (reorder) onReorder(dragged.id, reorder.orderIndex);
    }
    setDragId(null);
  };

  return (
    <div
      data-testid="outline-editor"
      className="flex h-full min-h-0 flex-col"
      style={{ backgroundColor: editorial ? c.canvas : PALETTE.cardBg }}
    >
      <div className={`flex-1 overflow-auto ${compact ? "px-3 py-3" : "px-6 py-5"}`}>
        {/* Editorial kicker — the mono `OUTLINE EDITOR — KEYBOARD FIRST` label
            above the card (`V_B23`). Roomy full-screen only; the split pane has
            its own pane header. Legacy renders no kicker (unchanged). */}
        {editorial && !compact && (
          <div
            data-testid="outline-kicker"
            className="mb-3 text-[10px] uppercase"
            style={{
              fontFamily: theme.fonts.mono,
              letterSpacing: "0.1em",
              color: c.muted,
            }}
          >
            Outline editor — keyboard first
          </div>
        )}
        {/* Editorial wraps the rows in a warm-paper card (`.t-card`); legacy
            renders the rows directly (byte-identical to today). */}
        <div
          className={editorial ? "rounded-lg border" : ""}
          style={
            editorial
              ? {
                  backgroundColor: c.cardBg,
                  borderColor: c.rule,
                  borderRadius: theme.radii.lg,
                  padding: compact ? "10px 12px" : "16px 18px",
                }
              : undefined
          }
        >
          {rows.map(({ node, depth }) => {
            const editing = node.id === editingNodeId;
            const placeholder = buildPlaceholder(node, roots);
            return (
              <div
                key={node.id}
                draggable={!editing}
                onDragStart={() => setDragId(node.id)}
                onDragOver={(e) => {
                  if (dragId) e.preventDefault();
                }}
                onDrop={() => handleDrop(node)}
                onDragEnd={() => setDragId(null)}
                className="relative"
              >
                <OutlineRow
                  node={node}
                  depth={depth}
                  members={members}
                  selected={node.id === selectedId}
                  editing={editing}
                  draft={editing ? draft : node.title}
                  placeholder={editing ? placeholder : undefined}
                  hasChildren={node.children.length > 0}
                  collapsed={collapsedSet.has(node.id)}
                  childCount={node.children.length}
                  onToggleCollapse={onToggleCollapse ? () => onToggleCollapse(node.id) : undefined}
                  onOpenNode={() => onOpenNode(node.id)}
                  onBeginEdit={() => onSetEditing(node.id)}
                  onDraftChange={setDraft}
                  onKeyDown={(e) => handleKeyDown(e, node)}
                  onBlur={() => handleBlur(node)}
                />
                {assignPickerFor === node.id && (
                  <div className="relative ml-8">
                    <AssignPopover
                      nodeTitle={node.title}
                      members={members}
                      assignedIds={node.assigneeIds}
                      onToggle={(mid, assign) => onAssignToggle(node.id, mid, assign)}
                      onClose={() => {
                        setAssignPickerFor(null);
                        // Return focus to the row so typing continues.
                        onSetEditing(node.id);
                      }}
                    />
                  </div>
                )}
                {/* "/" command menu (#1043) — change state / add a dependency.
                    Anchored under the row; closing returns focus to the row so
                    typing continues (Escape inside the menu, a choice, or a
                    click away). */}
                {commandMenuFor === node.id && (
                  <div className="relative ml-8">
                    <OutlineCommandMenu
                      current={node.state}
                      onSetState={
                        onSetState ? (state) => onSetState(node.id, state) : () => undefined
                      }
                      onAddDependency={onArmDependency ? () => onArmDependency(node.id) : undefined}
                      onClose={() => {
                        setCommandMenuFor(null);
                        // Return focus to the row so typing continues.
                        onSetEditing(node.id);
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* + outcome — adds a new root node, opened in edit mode. */}
        <button
          type="button"
          data-testid="outline-add-outcome"
          onClick={() => onCreate(rootCreateTarget(roots), { focusEmpty: true })}
          className="mt-3 text-[12px] transition-opacity hover:opacity-70"
          style={{
            color: editorial ? c.teal : PALETTE.teal,
            fontFamily: editorial ? theme.fonts.sans : undefined,
          }}
        >
          + outcome
        </button>
      </div>

      {/* Footer hint — gallery copy, verbatim (B2.3). EDITORIAL renders the keys
          as `Kbd` chips around the SAME literal tokens, so the strip's text
          content is byte-identical to the legacy plain-text line (the test
          asserts the exact copy). */}
      <div
        className="border-t px-6 py-2 text-[11px]"
        style={
          editorial
            ? { borderColor: c.rule, color: c.muted, fontFamily: theme.fonts.sans }
            : { borderColor: "#F0F0F0", color: "#9CA3AF" }
        }
        data-testid="outline-hint"
      >
        {editorial ? (
          <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1">
            <Kbd>↵</Kbd> new sibling <span>·</span> <Kbd>Tab</Kbd>/<Kbd>⇧Tab</Kbd> indent/outdent{" "}
            <span>·</span> <Kbd>@</Kbd> to assign{" "}
            {commandMenuAvailable && (
              <>
                <span>·</span> <Kbd>/</Kbd> for commands{" "}
              </>
            )}
            <span>·</span> drag to reorder
          </span>
        ) : (
          `↵ new sibling · Tab/⇧Tab indent/outdent · @ to assign${
            commandMenuAvailable ? " · / for commands" : ""
          } · drag to reorder`
        )}
      </div>
    </div>
  );
}

/** Resolve the visual sibling index of a node (drag-reorder target slot). */
function indexOfSibling(node: TreeNode, roots: readonly TreeNode[]): number {
  if (node.parentId === null) return roots.findIndex((r) => r.id === node.id);
  const parent = findNode(roots, node.parentId);
  return parent ? parent.children.findIndex((c) => c.id === node.id) : 0;
}

/**
 * The contextual placeholder shown in the active edit row (B2.3:
 * "type a task · Tab = sub-task of "<preceding sibling>""). When the row has a
 * preceding sibling, Tab would nest under it — name it so the gesture is
 * discoverable; otherwise a plain prompt.
 */
function buildPlaceholder(node: TreeNode, roots: readonly TreeNode[]): string {
  const target = indentTarget(roots, node);
  if (target) {
    const parent = findNode(roots, target.parentId ?? "");
    if (parent) return `type a task · Tab = sub-task of “${parent.title}”`;
  }
  return "type a task…";
}
